import {Component, Expressions, type Plugins} from '@packages/systems/wfdl';
import {
  isOverlayTargetAddressable,
  CMS_RICH_TEXT_OVERLAY_TARGET_SCOPE,
} from '@packages/systems/overlays';
import {
  isUserComponentInst,
  getAncestorProjectingElements,
  getComponentInstRootElement,
  getUserCustomizedElementLabel,
  pageComponentType,
} from '@packages/utilities/platform';
import {getElementWithAncestors} from '@packages/utilities/core-elements';
import {
  ElementComponentIcon,
  type RowItemProps,
} from '@packages/systems/spring';
import type {Expression, ExpressionElement} from '@packages/systems/wfdl-ast';
import {getName, namesEqual} from '@packages/systems/wfdl-Qualified';
import type {StyleBlockStateType} from '@packages/shared/LegacyStyleSystem/states/StyleBlockStateTypes';
import type {AssetState} from '@packages/systems/designer/assets';

type OverlayTargetOption = {
  id: string;
  name: string;
  colorVariant?: RowItemProps['colorVariant'];
  prefix?: RowItemProps['prefix'];
};
export type OverlayTargetOptions = {
  options: OverlayTargetOption[];
  hasValidOptions: boolean;
};
type OverlayTargetOptionsInput = {
  isTarget: (element: ExpressionElement) => boolean;
  missingLabel: string;
  render: Expression;
  components: Component.ComponentMap;
  plugins: Plugins.PluginsType;
  styleBlockState: StyleBlockStateType;
  assetState: AssetState;
  selectedId?: string;
  triggerProjectingAncestorIds?: readonly string[];
  isEditingDefault: boolean;
  // Limits the targets to these elements of `render`.
  candidates?: readonly ExpressionElement[];
};
type ContainingOverlayTargetInput = {
  isTarget: (element: ExpressionElement) => boolean;
  render: Expression;
  components: Component.ComponentMap;
  triggerId: string;
  // Limits the targets to these elements of `render`.
  candidates?: readonly ExpressionElement[];
};
type ContainingOverlayTargetScopeInput = ContainingOverlayTargetInput & {
  currentComponentType: Plugins.QualifiedElement;
  isCmsRichTextScope: boolean;
};
export type ContainingOverlayTarget = {
  targetId: string;
  targetComponent: string;
};

export const getProjectingAncestorIds = (
  elementId: string,
  render: Expression
): string[] =>
  getAncestorProjectingElements(elementId, render).map(
    (ancestor) => ancestor.id
  );

const isOverlayComponentInst = (
  components: Component.ComponentMap,
  element: ExpressionElement,
  isTarget: (element: ExpressionElement) => boolean
): boolean => {
  if (!isUserComponentInst(components, element)) return false;
  const root = getComponentInstRootElement(components)(element);
  return Boolean(root && isTarget(root));
};

export const getOverlayTargetOptions = ({
  render,
  components,
  plugins,
  styleBlockState,
  assetState,
  selectedId,
  triggerProjectingAncestorIds,
  isEditingDefault,
  candidates,
  isTarget,
  missingLabel,
}: OverlayTargetOptionsInput): OverlayTargetOptions => {
  if (isEditingDefault) {
    return {
      options: selectedId ? [{id: selectedId, name: missingLabel}] : [],
      hasValidOptions: false,
    };
  }

  const options: OverlayTargetOption[] = [];
  let hasValidOptions = false;
  const elements = candidates
    ? Object.fromEntries(candidates.map((element) => [element.id, element]))
    : Expressions.allElements(render);
  const isAddressable = (element: ExpressionElement): boolean =>
    !triggerProjectingAncestorIds ||
    isOverlayTargetAddressable({
      targetProjectingAncestorIds: getProjectingAncestorIds(element.id, render),
      triggerProjectingAncestorIds,
    });
  const isOfferable = (element: ExpressionElement): boolean =>
    element.id === selectedId || isAddressable(element);

  for (const key in elements) {
    const element = elements[key];
    if (!element) continue;

    if (isTarget(element)) {
      if (isOfferable(element)) {
        options.push({
          id: element.id,
          name: getUserCustomizedElementLabel(
            element,
            render,
            components,
            plugins,
            styleBlockState,
            assetState
          ),
        });
        if (isAddressable(element)) hasValidOptions = true;
      }
      continue;
    }

    if (
      isOverlayComponentInst(components, element, isTarget) &&
      isOfferable(element)
    ) {
      options.push({
        id: element.id,
        name: getUserCustomizedElementLabel(
          element,
          render,
          components,
          plugins,
          styleBlockState,
          assetState
        ),
        colorVariant: 'green',
        prefix: ElementComponentIcon,
      });
      if (isAddressable(element)) hasValidOptions = true;
    }
  }

  if (selectedId && !options.some((option) => option.id === selectedId)) {
    const selectedElement = elements[selectedId];
    options.push({
      id: selectedId,
      name: selectedElement
        ? getUserCustomizedElementLabel(
            selectedElement,
            render,
            components,
            plugins,
            styleBlockState,
            assetState
          )
        : missingLabel,
    });
  }

  options.sort(
    (a, b) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id)
  );

  return {options, hasValidOptions};
};

export const getOverlayTargetComponent = (
  currentComponentType: Plugins.QualifiedElement
): string =>
  namesEqual(currentComponentType, pageComponentType)
    ? 'page'
    : getName(currentComponentType);

export const getScopedOverlayTargetComponent = (
  currentOverlayTargetComponent: string,
  isCmsRichTextScope: boolean
): string =>
  isCmsRichTextScope
    ? CMS_RICH_TEXT_OVERLAY_TARGET_SCOPE
    : currentOverlayTargetComponent;

/**
 * Returns the nearest overlay containing the trigger that the trigger can
 * address, so a close button inside an overlay targets it by default. An
 * overlay component instance counts as containing its own link props.
 */
export const getContainingOverlayTargetId = ({
  render,
  components,
  triggerId,
  candidates,
  isTarget,
}: ContainingOverlayTargetInput): string | undefined => {
  const elementWithAncestors = getElementWithAncestors(render)(triggerId);
  const triggerProjectingAncestorIds = getProjectingAncestorIds(
    triggerId,
    render
  );

  for (let index = elementWithAncestors.length - 1; index >= 0; index--) {
    const element = elementWithAncestors[index];
    if (!element) continue;
    if (candidates && !candidates.some(({id}) => id === element.id)) continue;

    const isContainingTarget =
      isTarget(element) ||
      isOverlayComponentInst(components, element, isTarget);

    if (
      isContainingTarget &&
      isOverlayTargetAddressable({
        targetProjectingAncestorIds: getProjectingAncestorIds(
          element.id,
          render
        ),
        triggerProjectingAncestorIds,
      })
    ) {
      return element.id;
    }
  }

  return undefined;
};

export const getContainingOverlayTarget = ({
  currentComponentType,
  isCmsRichTextScope,
  ...input
}: ContainingOverlayTargetScopeInput): ContainingOverlayTarget | null => {
  const targetId = getContainingOverlayTargetId(input);

  return targetId
    ? {
        targetId,
        targetComponent: getScopedOverlayTargetComponent(
          getOverlayTargetComponent(currentComponentType),
          isCmsRichTextScope
        ),
      }
    : null;
};
